<template>
  <div class="promo-view container">
    <section
      v-if="promotion"
      class="promo-content"
    >
      <TexturedCard class="promo-card">
        <div class="promo-card__layout">
          <div class="promo-card__cover-wrap">
            <img
              v-if="coverUrl"
              :src="coverUrl"
              :alt="`${promotion.title} cover art`"
              loading="lazy"
              class="promo-card__cover"
            >
            <div
              v-else
              class="promo-card__cover-fallback"
            >
              {{ promotion.title }}
            </div>
          </div>

          <div class="promo-card__details">
            <header class="promo-card__header">
              <h1 class="promo-title">
                {{ promotion.title }}
              </h1>
              <p class="promo-subtitle">
                {{ promotion.artist }}
              </p>
            </header>

            <section
              v-if="socialPlatforms.length"
              class="promo-card__section"
            >
              <h2 class="promo-card__section-title">
                Streaming Services
              </h2>
              <SocialIcons
                :platforms="socialPlatforms"
                theme="mono"
                size="lg"
                spacing="tight"
              />
            </section>

            <section
              v-if="bandcampEmbedUrl"
              class="promo-card__section"
            >
              <h2 class="promo-card__section-title">
                Listen Here
              </h2>
              <div class="promo-card__embed">
                <iframe
                  :src="bandcampEmbedUrl"
                  title="Bandcamp player"
                  loading="lazy"
                  allow="autoplay"
                  seamless
                />
              </div>
            </section>
          </div>
        </div>

        <!--
        <div
          v-if="canCopy"
          class="promo-card__share-row"
        >
          <button
            type="button"
            class="promo-card__share-icon"
            :title="copied ? 'Share link copied' : 'Copy share link'"
            aria-label="Copy share link"
            @click="copyShareLink"
          >
            <Share2 class="promo-card__share-icon-svg" />
            <span class="sr-only">
              Copy share link
            </span>
          </button>
        </div>
        -->
      </TexturedCard>
    </section>

    <section
      v-else
      class="promo-empty ui-panel"
    >
      <h1 class="promo-empty__title">
        Promotion not found
      </h1>
      <p>
        No promotion matched
        <strong>{{ requestedSlug || 'the requested slug' }}</strong>.
      </p>
      <div
        v-if="firstPromotion"
        class="promo-empty__actions"
      >
        <a
          href="/promo"
          class="ui-button ui-button--primary ui-button--sm"
        >
          Open active promotion
        </a>
      </div>
      <ul
        v-if="promotions.length"
        class="promo-empty__list"
      >
        <li
          v-for="item in promotions"
          :key="item.slug"
        >
          <a :href="`/promo/${item.slug}`">{{ item.slug }}</a>
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, watch } from 'vue';
import { useRoute } from 'vue-router';
// import { Share2 } from 'lucide-vue-next'; // Temporarily hidden share button
import SocialIcons from '@/components/common/SocialIcons.vue';
import TexturedCard from '@/components/effects/TexturedCard.vue';
import promotionsData from '@/assets/promotions.json';
import { resolveImageUrl } from '@/utils/assetPaths';

const route = useRoute();

const promotions = Array.isArray(promotionsData.items) ? promotionsData.items : [];

const linkOrder = [
  'spotify',
  'apple',
  'youtube-music',
  'youtube',
  'soundcloud',
  'bandcamp',
  'tidal',
  'amazon',
  'itunes',
  'discogs',
  'tiktok',
  'x',
  'rss',
];

const platformAliases = {
  applemusic: 'apple',
  'apple-music': 'apple',
  amazonmusic: 'amazon',
  'amazon-music': 'amazon',
  youtubemusic: 'youtube-music',
  'youtube-music': 'youtube-music',
  ytmusic: 'youtube-music',
  youtube_music: 'youtube-music',
  xcom: 'x',
};

const displayLabelMap = {
  spotify: 'Spotify',
  apple: 'Apple Music',
  'youtube-music': 'YouTube Music',
  youtube: 'YouTube',
  soundcloud: 'SoundCloud',
  bandcamp: 'Bandcamp',
  tidal: 'TIDAL',
  amazon: 'Amazon Music',
  itunes: 'iTunes',
  discogs: 'Discogs',
  tiktok: 'TikTok',
  x: 'X',
  rss: 'RSS',
};

const socialNameMap = {
  'youtube-music': 'youtube music',
};

const firstPromotion = computed(() => promotions[0] || null);

const requestedSlug = computed(() =>
  typeof route.params.slug === 'string' ? route.params.slug.trim() : '',
);

const activeSlug = computed(() => {
  if (requestedSlug.value) return requestedSlug.value;
  if (promotionsData.activeSlug) return promotionsData.activeSlug;
  return firstPromotion.value?.slug || '';
});

const promotion = computed(
  () => promotions.find((item) => item.slug === activeSlug.value) || null,
);

const formatKey = (value) =>
  value
    .toString()
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());

const normalizePlatformKey = (value) =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

const canonicalPlatformKey = (value) => {
  const normalized = normalizePlatformKey(value);
  return platformAliases[normalized] || normalized;
};

const toDisplayLabel = (key) => displayLabelMap[key] || formatKey(key);

const normalizeLinks = (links) => {
  if (!links) return [];

  if (Array.isArray(links)) {
    return links
      .filter((entry) => entry?.url)
      .map((entry, index) => {
        const rawKey = entry.key || entry.platform || entry.name || `link-${index + 1}`;
        const key = canonicalPlatformKey(rawKey);
        return {
          key,
          label: entry.label || toDisplayLabel(key),
          url: entry.url,
        };
      })
      .filter((entry) => entry.url);
  }

  if (typeof links === 'object') {
    return Object.entries(links)
      .filter(([, url]) => typeof url === 'string' && url.trim())
      .map(([rawKey, url]) => {
        const key = canonicalPlatformKey(rawKey);
        return {
          key,
          label: toDisplayLabel(key),
          url,
        };
      });
  }

  return [];
};

const displayLinks = computed(() => {
  const links = normalizeLinks(promotion.value?.links);
  const rankedLinks = links.map((entry, index) => ({
    ...entry,
    sortOrder: linkOrder.indexOf(entry.key),
    index,
  }));

  rankedLinks.sort((a, b) => {
      const aOrder = a.sortOrder === -1 ? Number.MAX_SAFE_INTEGER : a.sortOrder;
      const bOrder = b.sortOrder === -1 ? Number.MAX_SAFE_INTEGER : b.sortOrder;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.index - b.index;
    });

  return rankedLinks.map((entry) => ({
    key: entry.key,
    label: entry.label,
    url: entry.url,
  }));
});

const socialPlatforms = computed(() =>
  displayLinks.value.map((link) => ({
    key: link.key,
    name: socialNameMap[link.key] || link.key,
    url: link.url,
  })),
);

const coverUrl = computed(() => resolveImageUrl(promotion.value?.cover));

const resolveBandcampEmbed = (item) => {
  const explicitEmbed = item?.bandcampEmbed;
  if (typeof explicitEmbed === 'string' && explicitEmbed.trim()) {
    return explicitEmbed.trim();
  }

  const trackId = item?.bandcampTrackId;
  if (typeof trackId === 'string' && trackId.trim()) {
    return `https://bandcamp.com/EmbeddedPlayer/v=2/track=${trackId.trim()}/size=large/tracklist=false/artwork=small/`;
  }

  if (typeof trackId === 'number') {
    return `https://bandcamp.com/EmbeddedPlayer/v=2/track=${trackId}/size=large/tracklist=false/artwork=small/`;
  }

  return '';
};

const bandcampEmbedUrl = computed(() => resolveBandcampEmbed(promotion.value));

const sharePath = computed(() => {
  if (!promotion.value?.slug) return '/promo';
  return `/promo/${promotion.value.slug}`;
});

const shareUrl = computed(() => {
  if (typeof window === 'undefined') return sharePath.value;
  return new URL(sharePath.value, window.location.origin).toString();
});

// const canCopy = computed(
//   () => typeof navigator !== 'undefined' && !!navigator.clipboard?.writeText,
// );
// const copied = ref(false);
// let copyTimeoutId = null;
//
// const copyShareLink = async () => {
//   if (!canCopy.value) return;
//
//   try {
//     await navigator.clipboard.writeText(shareUrl.value);
//     copied.value = true;
//
//     if (copyTimeoutId) {
//       window.clearTimeout(copyTimeoutId);
//     }
//
//     copyTimeoutId = window.setTimeout(() => {
//       copied.value = false;
//       copyTimeoutId = null;
//     }, 1600);
//   } catch (error) {
//     console.error('[PromotionView] Failed to copy share link:', error);
//   }
// };

const originalTitle = typeof document !== 'undefined' ? document.title : '';
const originalMetaState = new Map();
let canonicalCaptured = false;
let originalCanonicalHref = null;

const toAbsoluteUrl = (value) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  if (typeof window === 'undefined') return value;
  return new URL(value, window.location.origin).toString();
};

const rememberMeta = (attribute, key, tag) => {
  const selector = `meta[${attribute}="${key}"]`;
  if (!originalMetaState.has(selector)) {
    originalMetaState.set(selector, {
      attribute,
      key,
      content: tag ? tag.getAttribute('content') : null,
    });
  }
};

const upsertMeta = (attribute, key, content) => {
  if (typeof document === 'undefined' || !content) return;

  const selector = `meta[${attribute}="${key}"]`;
  let tag = document.head.querySelector(selector);
  rememberMeta(attribute, key, tag);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
};

const removeMeta = (attribute, key) => {
  if (typeof document === 'undefined') return;

  const selector = `meta[${attribute}="${key}"]`;
  const tag = document.head.querySelector(selector);
  if (!tag) return;

  rememberMeta(attribute, key, tag);
  tag.remove();
};

const upsertCanonical = (href) => {
  if (typeof document === 'undefined' || !href) return;

  let canonicalTag = document.head.querySelector('link[rel="canonical"]');
  if (!canonicalCaptured) {
    originalCanonicalHref = canonicalTag ? canonicalTag.getAttribute('href') : null;
    canonicalCaptured = true;
  }

  if (!canonicalTag) {
    canonicalTag = document.createElement('link');
    canonicalTag.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalTag);
  }

  canonicalTag.setAttribute('href', href);
};

watch(
  [promotion, coverUrl, shareUrl],
  ([item, cover, pageShareUrl]) => {
    if (typeof document === 'undefined') return;

    if (!item) {
      document.title = 'Promotion not found | Expected Maximum';
      return;
    }

    const titleCore = [item.title, item.artist].filter(Boolean).join(' - ');
    const pageTitle = titleCore
      ? `${titleCore} | Expected Maximum`
      : 'Promotion | Expected Maximum';
    const description =
      item.description ||
      `Listen to ${item.title || 'this release'} by ${item.artist || 'Expected Maximum'}.`;
    const absoluteCover = toAbsoluteUrl(cover);

    document.title = pageTitle;

    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', titleCore || pageTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', item.mediaType === 'video' ? 'video.other' : 'music.song');
    upsertMeta('property', 'og:url', pageShareUrl);
    upsertMeta('name', 'twitter:card', absoluteCover ? 'summary_large_image' : 'summary');
    upsertMeta('name', 'twitter:title', titleCore || pageTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:url', pageShareUrl);

    if (absoluteCover) {
      upsertMeta('property', 'og:image', absoluteCover);
      upsertMeta('name', 'twitter:image', absoluteCover);
    } else {
      removeMeta('property', 'og:image');
      removeMeta('name', 'twitter:image');
    }

    upsertCanonical(pageShareUrl);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  // if (copyTimeoutId) {
  //   window.clearTimeout(copyTimeoutId);
  //   copyTimeoutId = null;
  // }

  if (typeof document === 'undefined') return;

  document.title = originalTitle;

  originalMetaState.forEach(({ attribute, key, content }) => {
    const selector = `meta[${attribute}="${key}"]`;
    let tag = document.head.querySelector(selector);

    if (content === null) {
      if (tag) {
        tag.remove();
      }
      return;
    }

    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(attribute, key);
      document.head.appendChild(tag);
    }

    tag.setAttribute('content', content);
  });

  if (canonicalCaptured) {
    let canonicalTag = document.head.querySelector('link[rel="canonical"]');

    if (originalCanonicalHref === null) {
      if (canonicalTag) {
        canonicalTag.remove();
      }
    } else {
      if (!canonicalTag) {
        canonicalTag = document.createElement('link');
        canonicalTag.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalTag);
      }
      canonicalTag.setAttribute('href', originalCanonicalHref);
    }
  }
});
</script>

<style scoped>
.promo-view {
  display: grid;
  gap: 1.5rem;
}

.promo-content {
  display: grid;
  gap: 1rem;
}

.promo-title {
  margin: 0;
}

.promo-subtitle {
  margin: 0;
  color: var(--secondary-color);
  font-weight: 600;
}

.promo-card__layout {
  display: grid;
  grid-template-columns: minmax(220px, 320px) minmax(0, 1fr);
  gap: 1.5rem;
}

.promo-card__cover-wrap {
  width: 100%;
  aspect-ratio: 1 / 1;
  border: 1px solid var(--border-color);
  background-color: var(--hover-color);
  overflow: hidden;
}

.promo-card__cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.promo-card__cover-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1rem;
  color: var(--secondary-color);
  font-weight: 600;
}

.promo-card__details {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  height: 100%;
}

.promo-card__header {
  display: grid;
  gap: 0.35rem;
  text-align: center;
  justify-items: center;
}

.promo-card__section {
  display: grid;
  gap: 0.65rem;
}

.promo-card__section-title {
  margin: 0;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--secondary-color);
}

.promo-card__share-row {
  margin-top: auto;
  padding-top: 0.25rem;
  display: flex;
  justify-content: center;
}

.promo-card__share-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.15rem;
  border: 0;
  background-color: transparent;
  color: var(--link-color);
  cursor: pointer;
}

.promo-card__share-icon:hover,
.promo-card__share-icon:focus-visible {
  color: var(--accent-color);
  outline: none;
}

.promo-card__share-icon-svg {
  width: 18px;
  height: 18px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.promo-card__embed {
  line-height: 0;
}

.promo-card__embed iframe {
  display: block;
  width: 100%;
  height: 120px;
  border: 0;
  background: transparent;
}

.promo-empty {
  display: grid;
  gap: 0.75rem;
}

.promo-empty__title {
  margin: 0;
}

.promo-empty__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.promo-empty__list {
  margin: 0;
  padding-left: 1.2rem;
  display: grid;
  gap: 0.3rem;
}

@media (max-width: 860px) {
  .promo-card__layout {
    grid-template-columns: 1fr;
  }

  .promo-card__cover-wrap {
    max-width: 380px;
  }
}
</style>
